import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, sessionApi } from '../api/sessionApi';

interface SessionState {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
}

const initialState: SessionState = {
  user: null,
  isAuthenticated: false,
  isInitialized: false,
};

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },
    clearSession: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
  },
  extraReducers: (builder) => {
    // /session отдаёт только факт авторизации — профиль приходит из /me.
    builder.addMatcher(
      sessionApi.endpoints.getSession.matchFulfilled,
      (state, { payload }) => {
        state.isAuthenticated = payload.is_authenticated;
      }
    );

    builder.addMatcher(
      sessionApi.endpoints.getSession.matchRejected,
      (state) => {
        state.user = null;
        state.isAuthenticated = false;
      }
    );

    // Профиль с правами — единственный источник state.user.
    builder.addMatcher(
      sessionApi.endpoints.getMe.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
      }
    );

    builder.addMatcher(
      sessionApi.endpoints.getMe.matchRejected,
      (state) => {
        state.user = null;
      }
    );

    // login возвращает только CSRF; профиль подтянет refetch /me по инвалидации.
    builder.addMatcher(
      sessionApi.endpoints.login.matchFulfilled,
      (state) => {
        state.isAuthenticated = true;
      }
    );

    builder.addMatcher(
      sessionApi.endpoints.logout.matchFulfilled,
      (state) => {
        state.user = null;
        state.isAuthenticated = false;
      }
    );
  },
});

export const sessionReducer = sessionSlice.reducer;
export const { setInitialized, clearSession } = sessionSlice.actions;

export const selectUser = (state: { session: SessionState }) => state.session.user;
export const selectIsAuthenticated = (state: { session: SessionState }) => state.session.isAuthenticated;
export const selectIsInitialized = (state: { session: SessionState }) => state.session.isInitialized;
export const selectPermissions = (state: { session: SessionState }) => state.session.user?.permissions ?? [];
export const selectIsModerator = (state: { session: SessionState }) => state.session.user?.is_moderator ?? false;
